<template>
  <div style="padding: 0 10px 10px 10px; border-right: 1px solid var(--border-color);">
    <div id="toolbar" :style="`background:${$vuetify.theme.currentTheme.background}`">
      <v-btn class="mobile-toolbar-close" icon @click="$emit('close')" v-if="mobile">
        <v-icon large>mdi-close</v-icon>
      </v-btn>

      <template v-if="$route.path === '/methodology'">
        <h3>Table of Contents</h3>
        <v-divider class="mb-2"></v-divider>
        <div class="toc-item"><a href="#overview">Overview</a></div>
        <div class="toc-item"><a href="#summary">Summary of the Methodology</a></div>
        <div class="toc-item"><a href="#dataset">Dataset and Variables</a></div>
        <div class="toc-item"><a href="#prediction_framework">Prediction Framework</a></div>
        <div class="toc-item"><a href="#feature_selection">Feature Selection</a></div>
        <div class="toc-item"><a href="#algorithms">Algorithms</a></div>
        <div class="toc-item"><a href="#polls">Polls</a></div>
        <div class="toc-item"><a href="#election_simulations">Election Simulations</a></div>
      </template>
      <template v-else>
        <v-switch
          v-model="$vuetify.theme.dark"
          @change="toggleDarkMode($event)"
          inset
          hide-details
          style="margin: 10px 0 20px;"
          label="Dark Mode">
        </v-switch>

        <ToolbarLoginPrompt v-if="!$auth.authenticated" />

        <h4 class="toolbar-heading">Search by Race</h4>
        <v-text-field
          v-model="search"
          label="Search"
          outlined
          dense
          hide-details
          class="my-1"
        ></v-text-field>
        <div class="mt-3">
          <div v-if="filteredElections.length > 0">
            <toolbar-item
              v-if="senateElections.length > 0"
              title="Senate"
              :elections="senateElections"
              routeName="senate">
            </toolbar-item>
            <toolbar-item
              v-if="houseElections.length > 0"
              title="House"
              :elections="houseElections"
              routeName="house">
            </toolbar-item>
          </div>
          <div class="elections-no-items" v-if="filteredElections.length === 0 && search !== ''">No races</div>
        </div>

        <ToolbarItem
          :elections="elections.filter((election) => election.chamber.toLowerCase().includes('runoff'))"
          routeName="senate"
          title="Senate Runoff" />

        <h4 class="toolbar-heading">Toss-Up Races</h4>

        <toolbar-item
          title="Senate"
          :elections="elections.filter(e => e.chamber === 'senate' && (e.rating === 'TOSS UP' || e.rating === 'TOSSUP'))"
          routeName="senate">
        </toolbar-item>
        <toolbar-item
          title="House"
          :elections="elections.filter(e => e.chamber === 'house' && (e.rating === 'TOSS UP' || e.rating === 'TOSSUP'))"
          routeName="house">
        </toolbar-item>
      </template>

      <div id="sidebar-ad-container"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import ToolbarItem from './ToolbarItem'

const components = {
  ToolbarItem,
  ToolbarLoginPrompt: () => import("./ToolbarLoginPrompt"),
}

const computed = {
  ...mapGetters({
    mobile: 'ux/mobile',
    elections: 'elections/list'
  }),
  filteredElections () {
    let search = this.search.toLowerCase()
    if (search) {
      const elections = this.elections.filter(item => {
        let raceName = item.raceName.toLowerCase()
        let stateAbbrev = item.state.toLowerCase()
        // search full spelt out race
        const case1 = raceName.startsWith(search)
        // search state abbrev
        const case2 = stateAbbrev.startsWith(search)
        // search state abbrev and cd
        const case3 = `${stateAbbrev} ${item.cd}`.startsWith(search) || `${stateAbbrev}${item.cd}`.startsWith(search)
        // search state abbrev and cd (w/o leading zero)
        const cd = String(parseInt(item.cd))
        const case4 = `${stateAbbrev} ${cd}`.startsWith(search) || `${stateAbbrev}${cd}`.startsWith(search)
        // search by candidates
        const candidates = Object.values(item.electionJson.currentElection).map(c => c.name.toLowerCase()).toString()
        const case5 = candidates.includes(search) && search.length > 3
        return case1 || case2 || case3 || case4 || case5
      })
      return elections
    } else {
      return []
    }
  },
  houseElections () {
    return this.filteredElections.filter(e => e.chamber.toLowerCase() === 'house').sort((a, b) => {
      const aName = `${a.state} ${a.cd}`
      const bName = `${b.state} ${b.cd}`
      return aName.localeCompare(bName)
    })
  },
  senateElections () {
    return this.filteredElections.filter(e => e.chamber.toLowerCase() === 'senate').sort((a, b) => {
      return a.state.localeCompare(b.state)
    })
  },
  senateRunoffElections () {
    return this.filteredElections.filter(e => e.chamber.toLowerCase().includes("runoff")).sort((a, b) => {
      return a.state.localeCompare(b.state)
    })
  },
}

const methods = {
  ...mapActions({
    toggleDarkMode: 'persistentUx/toggleDarkMode'
  }),
  onScroll () {
    const toolbar = document.getElementById('toolbar')
    const headerHeight = document.getElementsByClassName('header-container')[0].clientHeight
    const scrollTop = window.scrollY
    if (toolbar) {
      if (scrollTop > headerHeight && !toolbar.classList.contains('sticky-toolbar')) {
        toolbar.classList.add('sticky-toolbar')
      }
      if (scrollTop < headerHeight && toolbar.classList.contains('sticky-toolbar')) {
        toolbar.classList.remove('sticky-toolbar')
      }
    }
  }
}

const watch = {
  $route () {
    this.$emit('close')
  }
}

export default {
  components,
  computed,
  methods,
  watch,
  data: () => ({
    search: ''
  }),
  mounted () {
    window.addEventListener('scroll', () => {
      this.onScroll()
    })

    const id = "div-gpt-ad-1657726772735-0";
    const bannerAdElement = document.getElementById(id);
    const bannerAdContainer = document.getElementById("sidebar-ad-container");
    bannerAdContainer.appendChild(bannerAdElement);
    bannerAdElement.style.display = "block";
  },
  beforeDestroy () {
    window.removeEventListener('scroll', () => {
      this.onScroll()
    })
  }
}
</script>

<style>
#toolbar {
  position: absolute;
  width: 160px;
}

.mobile-toolbar-close {
  position: fixed;
  right: 20px;
  z-index: 11;
}

.sticky-toolbar {
  top: 0;
}

.elections-no-items {
  font-size: 14px;
}

.toc-item {
  text-align: start;
  margin-bottom: 10px;
}

.toc-item a {
  color: var(--text-color) !important;
  text-decoration-color: var(--text-color) !important;
  font-weight: 400;
  letter-spacing: 0.02em;
}

.toolbar-heading {
  margin: 20px 0 10px;
}

@media only screen and (max-width: 960px) {
  #toolbar {
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    z-index: 10;
    padding: 20px;
    left: 50%;
    transform: translateX(-50%);
    overflow: auto;
  }

  .elections-list-title {
    font-size: 16px;
  }

  .elections-list-item {
    font-size: 18px;
    margin: 5px 0;
  }
}
</style>
